import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, Globe2, Info, Save, Send } from "lucide-react";
import { Modal, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import CmsTextEditor from "../../components/cms/CmsTextEditor";
import {
  create_cms_documents,
  get_cms_document_by_id,
  update_cms_documents,
} from "../../reduxData/cms/cmsAction";
import { PageHeader } from "./CmsDocumentComponents.js";
import { DOCUMENT_TYPES, VISIBILITIES, apiError } from "./cmsDocumentUtils";
import {
  EMPTY_DOCUMENT_FORM,
  LANGUAGE_CONFIG,
  buildDocumentPayload,
  getLanguageForErrors,
  isLanguageComplete,
  mapDocumentToForm,
  validateDocumentForm,
} from "./cmsDocumentFormHelpers";

import "../../css/cmsDocuments.css";

const DRAFT = "DRAFT";
const PUBLISHED = "PUBLISHED";

export default function DocumentForm({ newVersion = false }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY_DOCUMENT_FORM);
  const [activeLanguage, setActiveLanguage] = useState("EN");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(newVersion);
  const [savingStatus, setSavingStatus] = useState("");
  const [confirmationStatus, setConfirmationStatus] = useState("");

  useEffect(() => {
    if (!newVersion) return;

    const loadCurrentDocument = async () => {
      try {
        const document = await get_cms_document_by_id(id, dispatch);
        setForm(mapDocumentToForm(document));
      } catch (error) {
        toast.error(apiError(error, t("cmsDocuments.errors.loadCurrent")));
      } finally {
        setLoading(false);
      }
    };

    loadCurrentDocument();
  }, [dispatch, id, newVersion]);

  const updateField = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const validationErrors = validateDocumentForm(form, t);
    const languageWithError = getLanguageForErrors(validationErrors);

    setErrors(validationErrors);

    if (languageWithError) {
      setActiveLanguage(languageWithError);
    }

    return Object.keys(validationErrors).length === 0;
  };

  const submitDocument = async (status) => {
    setConfirmationStatus("");
    setSavingStatus(status);

    const payload = buildDocumentPayload(form, status, newVersion);

    try {
      if (newVersion) {
        await update_cms_documents(id, payload, dispatch);
      } else {
        await create_cms_documents(payload, dispatch);
      }

      const successMessage =
        status === PUBLISHED
          ? t("cmsDocuments.messages.published")
          : newVersion
            ? t("cmsDocuments.messages.draftVersionCreated")
            : t("cmsDocuments.messages.savedAsDraft");

      toast.success(successMessage);
      navigate(newVersion ? `/cms/documents/${id}/versions` : "/cms/documents");
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;

      if (backendErrors && typeof backendErrors === "object") {
        setErrors(backendErrors);
      }

      toast.error(apiError(error, t("cmsDocuments.errors.saveDocument")));
    } finally {
      setSavingStatus("");
    }
  };

  const requestSubmission = (status) => {
    if (!validateForm()) return;

    if (status === PUBLISHED || newVersion) {
      setConfirmationStatus(status);
      return;
    }

    submitDocument(status);
  };

  if (loading) {
    return (
      <main className="cmsd-page">
        <div className="cmsd-state">
          <Spinner />
          <p>{t("cmsDocuments.shared.loadingDocument")}</p>
        </div>
      </main>
    );
  }

  const languageConfig = LANGUAGE_CONFIG[activeLanguage];
  const titleError = errors[languageConfig.titleKey];
  const contentError = errors[languageConfig.contentKey];
  const backPath = newVersion ? `/cms/documents/${id}` : "/cms/documents";

  return (
    <main className="cmsd-page cmsd-form-page">
      <PageHeader
        title={t(newVersion ? "cmsDocuments.form.createNewVersion" : "cmsDocuments.actions.addDocument")}
        description={
          newVersion
            ? t("cmsDocuments.form.newVersionDescription")
            : t("cmsDocuments.form.createDescription")
        }
      >
        <Link className="btn btn-light" to={backPath}>
          <ArrowLeft size={17} />
          {t("common.back")}
        </Link>
      </PageHeader>

      {newVersion && (
        <div className="cmsd-info">
          <Info size={19} />
          <span>
            {t("cmsDocuments.form.currentRemainsActive")}
          </span>
        </div>
      )}

      <div className="cmsd-form-shell">
        <form
          className="cmsd-document-form"
          onSubmit={(event) => event.preventDefault()}
        >
          <BasicInformationSection
            form={form}
            errors={errors}
            newVersion={newVersion}
            onChange={updateField}
          />

          <BilingualContentSection
            form={form}
            activeLanguage={activeLanguage}
            languageConfig={languageConfig}
            titleError={titleError}
            contentError={contentError}
            onLanguageChange={setActiveLanguage}
            onChange={updateField}
          />

          <PublishActions
            savingStatus={savingStatus}
            onSaveDraft={() => requestSubmission(DRAFT)}
            onPublish={() => requestSubmission(PUBLISHED)}
          />
        </form>
      </div>
      <SubmissionConfirmation
        status={confirmationStatus}
        newVersion={newVersion}
        busy={Boolean(savingStatus)}
        onClose={() => setConfirmationStatus("")}
        onConfirm={() => submitDocument(confirmationStatus)}
      />
    </main>
  );
}

function SubmissionConfirmation({
  status,
  newVersion,
  busy,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  if (!status) return null;

  const publishing = status === PUBLISHED;
  const subject = t(newVersion ? "cmsDocuments.form.newVersionSubject" : "cmsDocuments.form.documentSubject");

  return (
    <Modal
      show
      centered
      backdrop={busy ? "static" : true}
      onHide={busy ? undefined : onClose}
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          {publishing ? t("cmsDocuments.form.publishSubject", { subject }) : t("cmsDocuments.form.saveDraftVersion")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {publishing
          ? t("cmsDocuments.form.publishConfirmation", { subject })
          : t("cmsDocuments.form.draftConfirmation")}
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-light"
          type="button"
          disabled={busy}
          onClick={onClose}
        >
          {t("common.cancel")}
        </button>
        <button
          className="btn cmsd-primary"
          type="button"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy && <Spinner size="sm" className="me-2" />}
          {t(publishing ? "cmsDocuments.actions.publishNow" : "cmsDocuments.actions.saveAsDraft")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function BasicInformationSection({ form, errors, newVersion, onChange }) {
  const { t } = useTranslation();
  return (
    <section className="cmsd-form-section">
      <SectionHeading
        number="01"
        title={t("cmsDocuments.form.basicInformation")}
        description={t("cmsDocuments.form.basicDescription")}
      />

      <div className="cmsd-form-grid">
        <Field label={t("cmsDocuments.form.documentType")} error={errors.type}>
          <select
            className="form-select"
            value={form.type}
            disabled={newVersion}
            onChange={(event) => onChange("type", event.target.value)}
          >
            <option value="">{t("cmsDocuments.form.selectDocumentType")}</option>
            {DOCUMENT_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`cmsDocuments.documentTypes.${option.value}`, { defaultValue: option.label })}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t("cmsDocuments.table.visibility")}
          error={errors.visibility}
          help={t(`cmsDocuments.visibilityHelp.${form.visibility.toLowerCase()}`)}
        >
          <select
            className="form-select"
            value={form.visibility}
            onChange={(event) => onChange("visibility", event.target.value)}
          >
            {VISIBILITIES.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`cmsDocuments.values.${option.value.toLowerCase()}`, { defaultValue: option.label })}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </section>
  );
}

function BilingualContentSection({
  form,
  activeLanguage,
  languageConfig,
  titleError,
  contentError,
  onLanguageChange,
  onChange,
}) {
  const { t } = useTranslation();
  return (
    <section className="cmsd-form-section">
      <SectionHeading
        number="02"
        title={t("cmsDocuments.form.bilingualContent")}
        description={t("cmsDocuments.form.bilingualDescription")}
      />

      <div className="cmsd-language-tabs">
        {Object.entries(LANGUAGE_CONFIG).map(([language, config]) => (
          <button
            type="button"
            className={activeLanguage === language ? "active" : ""}
            onClick={() => onLanguageChange(language)}
            key={language}
          >
            <span>{language}</span>
            <div>
              <strong>{t(`cmsDocuments.languages.${language.toLowerCase()}.name`)}</strong>
              <small>{t(`cmsDocuments.languages.${language.toLowerCase()}.description`)}</small>
            </div>
            {isLanguageComplete(form, language) && <Check size={16} />}
          </button>
        ))}
      </div>

      <div className="cmsd-content-editor">
        <div className="cmsd-editor-label">
          <Globe2 size={17} />
          <strong>{t("cmsDocuments.form.languageDocument", { language: t(`cmsDocuments.languages.${activeLanguage.toLowerCase()}.name`) })}</strong>
          <span>{activeLanguage}</span>
        </div>

        <Field label={t("cmsDocuments.form.documentTitle", { language: activeLanguage })} error={titleError}>
          <input
            className="form-control"
            value={form[languageConfig.titleKey]}
            placeholder={t(`cmsDocuments.languages.${activeLanguage.toLowerCase()}.titlePlaceholder`)}
            onChange={(event) =>
              onChange(languageConfig.titleKey, event.target.value)
            }
          />
        </Field>

        <Field
          label={t("cmsDocuments.form.documentContent", { language: activeLanguage })}
          error={contentError}
        >
          <div
            className={contentError ? "cmsd-editor is-invalid" : "cmsd-editor"}
          >
            <CmsTextEditor
              key={activeLanguage}
              value={form[languageConfig.contentKey]}
              placeholder={t(`cmsDocuments.languages.${activeLanguage.toLowerCase()}.contentPlaceholder`)}
              onChange={(value) => onChange(languageConfig.contentKey, value)}
            />
          </div>
        </Field>
      </div>
    </section>
  );
}

function PublishActions({ savingStatus, onSaveDraft, onPublish }) {
  const { t } = useTranslation();
  const isSaving = Boolean(savingStatus);

  return (
    <footer className="cmsd-publish-footer">
      <div>
        <strong>{t("cmsDocuments.form.ready")}</strong>
        <small>{t("cmsDocuments.form.readyHint")}</small>
      </div>

      <div>
        <button
          type="button"
          className="btn cmsd-draft-button"
          disabled={isSaving}
          onClick={onSaveDraft}
        >
          {savingStatus === DRAFT ? <Spinner size="sm" /> : <Save size={17} />}
          {t("cmsDocuments.actions.saveAsDraft")}
        </button>

        <button
          type="button"
          className="btn cmsd-primary"
          disabled={isSaving}
          onClick={onPublish}
        >
          {savingStatus === PUBLISHED ? (
            <Spinner size="sm" />
          ) : (
            <Send size={17} />
          )}
          {t("cmsDocuments.actions.publishDocument")}
        </button>
      </div>
    </footer>
  );
}

function SectionHeading({ number, title, description }) {
  return (
    <div className="cmsd-section-heading">
      <span>{number}</span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
  );
}

function Field({ label, error, help, children }) {
  const errorMessage = Array.isArray(error) ? error[0] : error;

  return (
    <label className="cmsd-field">
      <span>{label}</span>
      {children}
      {help && <small>{help}</small>}
      {errorMessage && <small className="text-danger">{errorMessage}</small>}
    </label>
  );
}
